import { PlasticsManufacturingRatesVersion } from '../models/plastics-manufacturing-rates-version.model';
import { QuoteStudioMachineKey, QuoteStudioOpKey } from '../models/quote-studio.model';

/*
 * Quote Studio rate metadata shared by the browser page and the Agent API.
 *
 * Rates are never stored on a recipe. Every $/hr, $/min and $/lb is read live
 * from the released PlasticsManufacturingRatesVersion; the recipe only keeps
 * the key. What lives here is the static metadata for those keys, so both the
 * page and the API agree on what a key means and which rate field prices it.
 */

/** one line of context per resin category; the label and price come from the rates */
export const QUOTE_STUDIO_MATERIAL_NOTES: Record<string, string> = {
    naturalPe: 'stock LLDPE, uncolored',
    blackRepro: 'reground, lowest cost',
    dryColoredPe: 'tumble blended pigment',
    preCompoundedColor: 'best color consistency',
    granites: 'speckled blend',
    xlpe: 'crosslinked, tanks',
    flameRetardant: 'flame retardant additive',
};

/** resin categories the studio does not offer, even if the rates carry them */
export const QUOTE_STUDIO_HIDDEN_MATERIAL_KEYS: string[] = ['other'];

/** oven cycle a new variation starts at, in minutes */
export const QUOTE_STUDIO_DEFAULT_CYCLE_MINUTES = 22;

/** the costing sheet's scrap percentage, which every model started from */
export const QUOTE_STUDIO_DEFAULT_SCRAP_PERCENT = 1.5;

/** a new variation runs the whole arm unless told otherwise */
export const QUOTE_STUDIO_DEFAULT_ARM_PERCENT = 100;

export const QUOTE_STUDIO_DEFAULT_MATERIAL_KEY = 'naturalPe';

export const QUOTE_STUDIO_DEFAULT_MACHINE_KEY: QuoteStudioMachineKey = 'largeMachineHourlyRate';

export interface QuoteStudioMachineDefinition {
    key: QuoteStudioMachineKey;
    label: string;
    note: string;
}

/** the two carousels — the hourly rate is filled in from the live rates */
export const QUOTE_STUDIO_MACHINES: QuoteStudioMachineDefinition[] = [
    { key: 'largeMachineHourlyRate', label: 'Ferry 280', note: 'large carousel · 156" arm swing' },
    { key: 'smallMachineHourlyRate', label: 'Ferry 220', note: 'small carousel · 120" arm swing' },
];

export const QUOTE_STUDIO_MACHINE_KEYS: QuoteStudioMachineKey[] = QUOTE_STUDIO_MACHINES.map(machine => machine.key);

export interface QuoteStudioOp {
    key: QuoteStudioOpKey;
    label: string;
    /** the field on the manufacturing rates version that prices a minute of this op */
    rateField: keyof PlasticsManufacturingRatesVersion;
}

/**
 * The secondary operations, each priced by the rate field of the same name. The
 * rates carry exactly these and nothing else, so no operation borrows another
 * one's rate the way deflashing and precision trimming used to.
 *
 * The op KEYS are persisted inside every variation's `ops` map, so they keep the
 * spelling they were written with; the labels and the rate fields are what had to
 * line up with the quote.
 */
export const QUOTE_STUDIO_OPS: QuoteStudioOp[] = [
    { key: 'basicTrimmingCostPerMinute', label: 'Basic trimming', rateField: 'basicTrimmingCostPerMinute' },
    { key: 'deflashingCostPerMinute', label: 'Deflashing', rateField: 'deflashingCostPerMinute' },
    { key: 'precisionFinishingCostPerMinute', label: 'Precision trimming', rateField: 'precisionTrimmingCostPerMinute' },
    { key: 'flamePolishCostPerMinute', label: 'Flame polish', rateField: 'flamePolishCostPerMinute' },
    { key: 'assemblyCostPerMinute', label: 'Assembly', rateField: 'assemblyCostPerMinute' },
    { key: 'packageCostPerMinute', label: 'Packaging', rateField: 'packagingCostPerMinute' },
    // leak tests, extra inspections, anything the customer's quality plan adds beyond a standard check
    { key: 'qualityTestingCostPerMinute', label: 'Advanced quality checks & testing', rateField: 'qualityTestingCostPerMinute' },
    // two-part foam poured into a part, e.g. Graymills' foamed sump tanks
    { key: 'foamFillingCostPerMinute', label: 'Foam filling', rateField: 'foamFillingCostPerMinute' },
    { key: 'otherCostPerMinute', label: 'Other', rateField: 'otherCostPerMinute' },
];

export const QUOTE_STUDIO_OP_KEYS: QuoteStudioOpKey[] = QUOTE_STUDIO_OPS.map(op => op.key);
